import { DealerSummary } from '../types';
import COUNTRY_CODES from '../types/countryCodes';
import DISTANCE_UNITS from '../types/distanceUnits';
import TradeDetails from './TradeDetails';
import { GridCardVariant, ListVariant } from '../types/trade';

// TODO: merge with existing TradeSummary type
export interface ITradeSummary {
  id: number;
  mileage: number;
  odometerType: string;
  transmission: string;
  isAsIs: boolean;
  hasDisclosures: boolean;
  isLanded: boolean;
  distance: string;
  isWatchlisted: boolean;
  dealer: DealerSummary;
  trim: string;
}

export const getTransmissionType = transmissionId => {
  switch (transmissionId) {
    case 0:
      return 'Auto';
    case 1:
      return 'Manual';
    default:
      return '';
  }
};

const getThumbnail = t => {
  return t?.thumb || t?.tradeVehicleSummary?.thumbnailUrl;
};

const getYear = t => {
  return t?.year || t?.tradeVehicleSummary?.vehicleSummary?.modelYear;
};

const getTrim = t => {
  return t?.trim || t.tradeVehicleSummary.vehicleSummary.trim;
};

const getDistance = (t, countryCode) => {
  const distance = t?.mileage || t?.distance;
  return distance === -1
    ? null
    : `${distance} ${
        countryCode === COUNTRY_CODES.CA ? DISTANCE_UNITS.KM : DISTANCE_UNITS.MI
      }`;
};

const mapTradeSummaries = (
  tradeSummaries = [],
  countryCode,
  manufacturers,
  dealerSummaries
): ITradeSummary[] =>
  tradeSummaries.map(t => {
    const autoLaunch = {
      date: '',
      time: ''
    };

    if (t?.statusDates?.autolaunch) {
      const date = new Date(t.statusDates.autolaunch);
      autoLaunch.date = new Intl.DateTimeFormat('en-CA', {
        month: 'short',
        day: 'numeric'
      }).format(date);
      autoLaunch.time = new Intl.DateTimeFormat('en-CA', {
        hour: 'numeric',
        minute: 'numeric'
      }).format(date);
    }

    return {
      ...t,
      id: t?.tradeId || t?.id,
      auctionSummary: t.auctionSummary
        ? {
            ...t.auctionSummary,
            proxyAmount:
              t.auctionSummary.proxyAmount &&
              t.auctionSummary.proxyAmount <= t.auctionSummary.maxBid
                ? null
                : t.auctionSummary.proxyAmount
          }
        : undefined,
      thumbnailUrl: getThumbnail(t),
      year: getYear(t),
      make:
        t?.make ||
        TradeDetails.getManufacturerName(
          t.tradeVehicleSummary.vehicleSummary,
          manufacturers
        ),
      model: t?.model || t.tradeVehicleSummary.vehicleSummary.model.displayName,
      mileage: t.mileage,
      odometerType:
        t.odometerType === 0 ? DISTANCE_UNITS.KM : DISTANCE_UNITS.MI,
      transmission: getTransmissionType(
        (t?.transmissionType || t?.transmission).id
      ),
      distance: getDistance(t, countryCode),
      autoLaunch,
      isWatchlisted: t.isWatchlisted,
      dealer:
        t?.dealer ||
        dealerSummaries.find(dealerSummary => t.dealerId === dealerSummary.id),
      trim: getTrim(t),
      saleEventId: t.saleEventId,
      saleEventSummary: t.saleEventSummary
    };
  });

/*
  In grid view, a separate array element is needed for sale event start and end cards.
    Why? The cards are styled based on their position on the grid, we calculate the position
    based on their index in the array.
  To accomplish this, this function reduces over the tradeSummaries and returns an updated array.
 */
const mapGridViewTradeSummaries = (
  tradeSummaries = [],
  showSaleEvent: Boolean
) =>
  // Note that the empty object concatenated is the "placeHolderTrade", It's added to help
  // make the logic a bit cleaner.
  tradeSummaries.concat([{}]).reduce(
    (acc, currTrade, i) => {
      const { prevSaleEventId, updatedTradeSummaries } = acc;
      const { saleEventId } = currTrade;
      const isPlaceholderTrade = i === tradeSummaries.length;

      // This if block is used to add sale event start/end objects to tradeSummaries
      if (prevSaleEventId !== saleEventId && showSaleEvent) {
        // If the previous trade has a sale event id, a sale event end object is pushed
        // and it includes the previous trades tradeSummary.
        if (prevSaleEventId !== null) {
          const prevTrade = isPlaceholderTrade
            ? tradeSummaries[tradeSummaries.length - 1]
            : tradeSummaries[i - 1];
          updatedTradeSummaries.push({
            tradeSummary: prevTrade,
            type: GridCardVariant.EVENT_END
          });
        }

        // If the current trade has a sale event id, a sale event start object is pushed
        // and it includes the current trades tradeSummary.
        if (saleEventId !== null && !isPlaceholderTrade) {
          updatedTradeSummaries.push({
            tradeSummary: currTrade,
            type: GridCardVariant.EVENT_START
          });
        }
      }

      // Here we push the actual trade object to the updatedTradeSummaries
      if (!isPlaceholderTrade) {
        updatedTradeSummaries.push({
          tradeSummary: currTrade,
          type:
            saleEventId && showSaleEvent
              ? GridCardVariant.EVENT_TRADE
              : GridCardVariant.NON_EVENT_TRADE
        });
      }

      return {
        prevSaleEventId: saleEventId,
        updatedTradeSummaries
      };
    },
    {
      prevSaleEventId: null,
      updatedTradeSummaries: []
    }
  ).updatedTradeSummaries;

// Maintain the number of trades returned in the current page for the list of Sale Events.
const mapSaleEventSummaries = (tradeSummaries = []) => {
  const saleEventSummaries = [];
  tradeSummaries.forEach(tradeSummary => {
    if (tradeSummary.saleEventId && tradeSummary.saleEventSummary) {
      const saleEventSummaryIdx = saleEventSummaries.findIndex(
        saleEvent => saleEvent.id === tradeSummary.saleEventId
      );
      if (saleEventSummaryIdx !== -1) {
        saleEventSummaries[saleEventSummaryIdx].count += 1;
        saleEventSummaries[saleEventSummaryIdx].totalCount =
          tradeSummary.saleEventSummary.numberOfTrades;
      } else {
        saleEventSummaries.push({
          id: tradeSummary.saleEventId,
          count: 1,
          totalCount: tradeSummary.saleEventSummary.numberOfTrades
        });
      }
    }
  });
  return saleEventSummaries;
};

class TradeList {
  dealers: DealerSummary[];

  tradeSummaries: ITradeSummary[];

  gridTradeSummaries: [];

  tradeCount: number;

  tradeTotal: number;

  manufacturers: any;

  saleEventSummaries: any;

  // TODO type information
  filterCriteria;

  constructor(
    tradeSummaries,
    dealerSummaries,
    tradeCount,
    tradeTotal,
    countryCode,
    manufacturers: any,
    details,
    listVariant,
    showSaleEvent
  ) {
    this.dealers = dealerSummaries || [];
    this.tradeSummaries = mapTradeSummaries(
      tradeSummaries,
      countryCode,
      manufacturers,
      dealerSummaries
    );
    this.gridTradeSummaries = [];

    this.tradeCount = tradeCount;

    this.tradeTotal = tradeTotal;

    this.saleEventSummaries = mapSaleEventSummaries(this.tradeSummaries);

    if (listVariant === ListVariant.GRID) {
      this.gridTradeSummaries = mapGridViewTradeSummaries(
        this.tradeSummaries,
        showSaleEvent
      );
    }

    this.filterCriteria = { id: details?.id, criteria: details?.criteria };
  }
}

export default TradeList;
